import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import type { AttendanceStatus } from '../types/attendance';

interface TrackEventParams {
  eventType: string;
  user?: User | null;
  userType?: 'parent' | 'admin' | 'anonymous' | 'staff';
  entityType?: string;
  entityId?: string | number;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  success?: boolean;
  errorMessage?: string;
  processingTimeMs?: number;
  email?: string;
}

// Generate a session ID for tracking user sessions
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Get or create session ID from sessionStorage
export function getSessionId(): string {
  const existingSessionId = sessionStorage.getItem('analytics_session_id');
  if (existingSessionId) {
    return existingSessionId;
  }
  
  const newSessionId = generateSessionId();
  sessionStorage.setItem('analytics_session_id', newSessionId);
  return newSessionId;
}

// Track events that have been logged in this session to prevent duplicates
const sessionEventTracker = new Set<string>();

// Generate a unique key for deduplication
function generateEventKey(eventType: string, entityId?: string | number, userId?: string): string {
  return `${eventType}:${entityId || 'null'}:${userId || 'anonymous'}`;
}

/**
 * Who an event was about, as names rather than ids.
 *
 * The analytics Details column resolves `entity_id` at read time, but an unenroll cascade
 * hard-deletes invoices and enrollments: hundreds of references already in the trail point
 * at rows that no longer exist and can never be named again. Copying the names in at emit
 * time is what keeps a row readable after its subject is gone — and it records the name as
 * it stood at the time, which is what an audit trail should say anyway.
 *
 * Empty names are omitted rather than written as `null`, so a resolver still gets its turn.
 */
export function subjectMetadata(subject: {
  studentName?: string | null;
  programName?: string | null;
}): Record<string, string> {
  const metadata: Record<string, string> = {};
  if (subject.studentName) metadata.student_name = subject.studentName;
  if (subject.programName) metadata.program_name = subject.programName;
  return metadata;
}

// Main event tracking function with fail-safe behavior
export function trackEvent(params: TrackEventParams): void {
  // Make analytics call completely non-blocking
  setTimeout(() => {
    (async () => {
      try {
        const {
          eventType,
          user,
          userType = 'parent',
          entityType,
          entityId,
          sessionId,
          metadata = {},
          success = true,
          errorMessage,
          processingTimeMs,
          email
        } = params;

        // Check for session-level deduplication for critical events
        const criticalEvents = [
          'payment_start',
          'payment_complete',
          'invoice_view',
          // Waiver events can be emitted from StrictMode double-invoked effects or rapid clicks
          'waiver_view',
          'waiver_submit',
        ];
        if (criticalEvents.includes(eventType)) {
          const eventKey = generateEventKey(eventType, entityId, user?.id);
          if (sessionEventTracker.has(eventKey)) {
            console.log(`Skipping duplicate ${eventType} event for session`);
            return;
          }
          sessionEventTracker.add(eventKey);
        }

        // Use default session ID if none provided
        const finalSessionId = sessionId || getSessionId();
        
        // Convert entityId to string if it's a number
        const finalEntityId = entityId ? String(entityId) : undefined;
        
        // Determine user type based on context
        let finalUserType = userType;
        if (user && !userType) {
          finalUserType = user.email?.includes('@admin.') ? 'admin' : 'parent';
        }
        
        // Deliberately NOT re-stating the browser, URL, referrer or timestamp here: each is
        // already its own column below (`user_agent`, `page_url`, `referrer`, `created_at`).
        // Duplicating them cost roughly 300 bytes of jsonb on every row of the highest-volume
        // table in the database. Readers use the columns, and fall back to these keys for rows
        // written before this change.
        const enrichedMetadata = { ...metadata };

        // Call the database function
        const { error } = await supabase.rpc('track_event', {
          p_event_type: eventType,
          p_user_id: user?.id || null,
          p_user_type: finalUserType,
          p_entity_type: entityType || null,
          p_entity_id: finalEntityId || null,
          p_session_id: finalSessionId,
          p_metadata: enrichedMetadata,
          // Always null: a browser cannot read its own public IP, and the column is kept
          // only because dropping it from a large table buys nothing.
          p_ip_address: null,
          p_user_agent: navigator.userAgent,
          p_referrer: document.referrer || null,
          p_page_url: window.location.href,
          p_success: success,
          p_error_message: errorMessage || null,
          p_processing_time_ms: processingTimeMs || null,
          p_email: email || user?.email || null
        });

        if (error) {
          console.warn('Analytics tracking failed:', error);
        }
      } catch (error) {
        console.warn('Analytics tracking error:', error);
      }
    })();
  }, 0);
}

// ── Brand profile ─────────────────────────────────────────────────────────────

/**
 * Why the active brand profile changed.
 *
 * Split into what the parent *did* and what was done *for* them, because the two answer
 * different questions. `switcher` and `signup` measure demand — a family deliberately
 * reaching for the other catalog. `link` and `landing` measure the routing working: a parent
 * who opened a Kaleido invoice and was moved to the Kaleido profile so the page made sense.
 * Counting them together would read every branded email as interest in switching.
 */
export type BrandSwitchSource = 'switcher' | 'signup' | 'link' | 'landing';

/** Sources the parent asked for by hand; everything else the portal decided on their behalf. */
const ACTIVE_BRAND_SWITCH_SOURCES: ReadonlySet<BrandSwitchSource> = new Set<BrandSwitchSource>([
  'switcher',
  'signup',
]);

/**
 * Record a change of brand profile.
 *
 * Emitted from the single funnel in `BrandProfileProvider`, never from a call site, so a
 * switch cannot be counted twice or missed by a path added later. The caller has already
 * established that the brand really changed.
 *
 * `trigger` is stored beside `source` although it is derivable from it: a query asking how
 * many parents chose to switch should not have to know which of four source values are the
 * deliberate ones, and it stays correct when a fifth is added.
 *
 * **No `entityId`.** `app_events.entity_id` and `track_event(p_entity_id)` are `uuid`, and a
 * brand key is not one: Postgres rejects it while binding the argument, before the function's
 * own fail-open handler can run, so the whole event is lost. The brand lives in the metadata.
 */
export function trackBrandSwitch(params: {
  from: string;
  to: string;
  source: BrandSwitchSource;
  user?: User | null;
}): void {
  trackEvent({
    eventType: 'brand_switch',
    user: params.user,
    metadata: {
      from_brand: params.from,
      to_brand: params.to,
      source: params.source,
      trigger: ACTIVE_BRAND_SWITCH_SOURCES.has(params.source) ? 'active' : 'passive',
    },
  });
}

// ── Attendance audit ──────────────────────────────────────────────────────────
//
// Absence lives in two mutable fields on a single attendance row, so the row can only ever
// answer "is this student absent right now". These events are the history behind it: who
// marked a child absent, who took that back, and when. A teacher can flip the same row more
// than once in an afternoon (marked absent, turns up late, checked in), which is exactly the
// sequence a pair of "…_by" columns would flatten into its last step.
//
// Deliberately write-only: nothing in the teacher or admin UI reads these back. They exist
// for the record and for Admin Search.

/** Everything an absence event needs to identify what was acted on, and by whom. */
export interface AbsenceEventContext {
  recordId: string;
  studentId: string;
  programId: string;
  sessionDate: string;
  /** Display name of the acting staff member, snapshotted the way the attendance columns are. */
  actorName?: string | null;
  /** Which portal the action came from. Teachers today; the admin app may gain it later. */
  actorRole?: 'staff' | 'admin';
}

/**
 * Emit an event attributed to whoever is signed in.
 *
 * The mutation hooks that call this have no `user` in hand, so the acting user is resolved
 * here rather than threaded through every caller. Failures are swallowed: an audit event
 * must never take the write it describes down with it.
 */
function trackAsCurrentUser(params: Omit<TrackEventParams, 'user'>): void {
  supabase.auth.getUser()
    .then(({ data }) => {
      trackEvent({ ...params, user: data.user });
    })
    .catch(() => {
      // Unauthenticated or an auth hiccup — nothing to attribute, nothing to log.
    });
}

function trackAbsenceEvent(
  eventType: string,
  ctx: AbsenceEventContext,
  metadata: Record<string, unknown>,
): void {
  trackAsCurrentUser({
    eventType,
    userType: ctx.actorRole ?? 'staff',
    entityType: 'attendance_record',
    entityId: ctx.recordId,
    metadata: {
      student_id: ctx.studentId,
      program_id: ctx.programId,
      session_date: ctx.sessionDate,
      actor_name: ctx.actorName ?? null,
      ...metadata,
    },
  });
}

// ── Pickup audit ──────────────────────────────────────────────────────────────
//
// Two separate records, both of which the row alone cannot answer:
//
// 1. A child released to an adult who was *not* on the authorized list. The attendance row
//    keeps `picked_up_by_name` / `pickup_is_authorized`, but only for the latest session —
//    and the question worth asking ("how often do we hand a child to someone unlisted?")
//    spans sessions.
// 2. Who was ever authorized to collect a child. `student_authorized_pickups` rows are hard
//    deleted, so removing an adult today erases the fact that they were ever permitted.
//
// Routine check-outs are deliberately *not* tracked. This is the only action in the portal
// that happens once per child per session — roughly a hundred rows a day at full enrollment,
// on the largest table in the database — and every one of them restates what the admin
// pickup view already reads off `attendance_records` (`picked_up_by_name`, `picked_up_at`,
// `pickup_recorded_by_name`). Only the exception is worth a row, so only the exception gets
// one; see `useMarkPickedUp`, which holds the gate.
//
// The parent's banner dismissal (`dismiss_pickup_banner`) is deliberately not tracked: it
// stamps `parent_dismissed_at` and destroys nothing.

/** A student was released to an adult who was not on the authorized list. */
export interface PickupRecordedContext {
  recordId: string;
  studentId: string;
  studentName?: string | null;
  programId: string;
  sessionDate: string;
  /** The adult who collected the child, as typed by staff. */
  pickedUpByName: string;
  /**
   * Always false — staff typed a name instead of choosing from the authorized list. Carried
   * explicitly so the row is self-describing to anyone reading the log without this context.
   */
  isAuthorized: boolean;
  /** Staff member who released the child, snapshotted the way the row's columns are. */
  actorName?: string | null;
}

/** An adult was added to, or removed from, a student's authorized pickup list. */
export interface AuthorizedPickupContext {
  studentId: string;
  studentName?: string | null;
  /** The row's id — present on removal, absent on add (the insert does not return it). */
  pickupId?: string | null;
  name: string;
  relationship?: string | null;
  phone?: string | null;
  /**
   * Who is doing it. The list started out parent-managed, so 'parent' stays the default;
   * the admin portal manages the same rows on a family's behalf and must not be logged as
   * the family having done it themselves.
   */
  actorType?: 'parent' | 'admin';
}

/**
 * Last four digits only. Enough to tell two same-named contacts apart in the log without
 * copying a reachable phone number into a table read by every admin.
 */
function phoneLast4(phone?: string | null): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
}

function trackAuthorizedPickupEvent(eventType: string, ctx: AuthorizedPickupContext): void {
  trackAsCurrentUser({
    eventType,
    userType: ctx.actorType ?? 'parent',
    entityType: 'student',
    entityId: ctx.studentId,
    metadata: {
      student_name: ctx.studentName ?? null,
      pickup_id: ctx.pickupId ?? null,
      pickup_name: ctx.name,
      relationship: ctx.relationship || null,
      phone_last4: phoneLast4(ctx.phone),
    },
  });
}

// ── Flex plan audit ───────────────────────────────────────────────────────────
//
// A flex month plan *is* the invoice: the month bills the confirmed days times the per-day
// rate. `flex_month_plans` keeps only the current day set and an `updated_by` string, so a
// parent who trims four days off a locked-tomorrow month leaves nothing behind explaining
// why the invoice shrank. The diff is recorded here rather than reconstructed later.

export interface FlexPlanSaveContext {
  enrollmentId: string;
  planMonth: string;
  /** The day set now confirmed. */
  dates: string[];
  /** What the plan held before the edit; omitted when the caller never loaded one. */
  previousDates?: string[] | null;
  /** Status the server returned after the save (`draft`, `locked`, …). */
  status?: string | null;
  /** Admins may edit past the lock; parents may not. */
  allowLocked?: boolean;
  actorType?: 'parent' | 'admin';
}

/** Everything an event needs to explain a change to a parent profile. */
export interface ProfileUpdateContext {
  parentId: string;
  parentEmail?: string | null;
  parentName?: string | null;
  /** Field names only — never the values, which are the PII. */
  changedFields: string[];
}

// Convenience functions for specific event types
export const analytics = {
  /** A student was marked absent. */
  trackAbsenceSet: (params: AbsenceEventContext & {
    reason: string | null;
    /** What the row said before — `checked_in` means this corrected a mistaken check-in. */
    previousStatus: AttendanceStatus;
  }) => trackAbsenceEvent('attendance_absence_set', params, {
    reason: params.reason ?? null,
    previous_status: params.previousStatus,
  }),

  /**
   * An absence was retracted — either undone outright, or overtaken by a late check-in,
   * which clears `absence_reason` just the same. Both are recorded so no absence in the log
   * is left looking as though it was never released.
   */
  trackAbsenceCleared: (params: AbsenceEventContext & {
    previousReason: string | null;
    clearedVia: 'undo' | 'check_in';
  }) => trackAbsenceEvent('attendance_absence_cleared', params, {
    previous_reason: params.previousReason ?? null,
    cleared_via: params.clearedVia,
  }),

  /** A student was released to an adult who was not on their authorized pickup list. */
  trackUnauthorizedPickup: (params: PickupRecordedContext) => trackAsCurrentUser({
    eventType: 'attendance_pickup_unauthorized',
    userType: 'staff',
    entityType: 'attendance_record',
    entityId: params.recordId,
    metadata: {
      student_id: params.studentId,
      student_name: params.studentName ?? null,
      program_id: params.programId,
      session_date: params.sessionDate,
      picked_up_by: params.pickedUpByName,
      is_authorized: params.isAuthorized,
      actor_name: params.actorName ?? null,
    },
  }),

  /**
   * A parent checked their own child out from the pickup screen. One per child. `via` is the
   * entrance — the QR code at the door or the app — which is the rollout's main question.
   */
  trackParentCheckOut: (params: {
    recordId: string;
    studentId: string | null;
    studentName?: string | null;
    via: 'qr' | 'app';
  }) => trackAsCurrentUser({
    eventType: 'attendance_parent_checkout',
    userType: 'parent',
    entityType: 'attendance_record',
    entityId: params.recordId,
    metadata: {
      student_id: params.studentId,
      ...subjectMetadata({ studentName: params.studentName }),
      via: params.via,
    },
  }),

  /**
   * Staff put a parent's self check-out back to checked in — the child was still in the room.
   * The count of these against `attendance_parent_checkout` is the measure of whether a tap
   * with no teacher in the loop can be trusted.
   */
  trackParentCheckOutUndone: (params: {
    recordId: string;
    studentId: string;
    studentName?: string | null;
    programId: string;
    sessionDate: string;
    actorName?: string | null;
  }) => trackAsCurrentUser({
    eventType: 'attendance_parent_checkout_undone',
    userType: 'staff',
    entityType: 'attendance_record',
    entityId: params.recordId,
    metadata: {
      student_id: params.studentId,
      ...subjectMetadata({ studentName: params.studentName }),
      program_id: params.programId,
      session_date: params.sessionDate,
      actor_name: params.actorName ?? null,
    },
  }),

  /**
   * A flex month plan was confirmed, by either portal. The day counts and the diff are what
   * make the resulting invoice explainable after the fact.
   */
  trackFlexPlanSave: (params: FlexPlanSaveContext) => {
    const previous = params.previousDates ?? null;
    const currentSet = new Set(params.dates);
    const previousSet = new Set(previous ?? []);
    return trackAsCurrentUser({
      eventType: 'flex_plan_save',
      userType: params.actorType ?? 'parent',
      entityType: 'enrollment',
      entityId: params.enrollmentId,
      metadata: {
        plan_month: params.planMonth,
        day_count: params.dates.length,
        // Null, not zero: "we never knew what it held before" and "nothing changed" are
        // different answers, and only one of them is evidence.
        previous_day_count: previous ? previous.length : null,
        days_added: previous ? params.dates.filter(d => !previousSet.has(d)) : null,
        days_removed: previous ? previous.filter(d => !currentSet.has(d)) : null,
        status: params.status ?? null,
        allow_locked: params.allowLocked ?? false,
      },
    });
  },

  /**
   * A one-line admin write event.
   *
   * The admin surfaces below each perform a single kind of change and have no `user` in
   * hand — a bespoke helper per surface would be six near-identical wrappers. The acting
   * admin is resolved here; the caller supplies only what makes its action identifiable.
   */
  trackAdminAction: (
    eventType: string,
    params: {
      entityType?: string;
      entityId?: string | number;
      metadata?: Record<string, unknown>;
    } = {},
  ) => trackAsCurrentUser({
    eventType,
    userType: 'admin',
    entityType: params.entityType,
    entityId: params.entityId,
    metadata: params.metadata ?? {},
  }),

  /** An admin edited a parent's profile. Mirrors the parent's own `parent_profile_update`. */
  trackAdminProfileUpdate: (params: ProfileUpdateContext) => trackAsCurrentUser({
    eventType: 'parent_profile_update',
    userType: 'admin',
    entityType: 'parent',
    entityId: params.parentId,
    metadata: {
      parent_email: params.parentEmail ?? null,
      parent_name: params.parentName ?? null,
      changed_fields: params.changedFields,
      edited_by_admin: true,
    },
  }),

  /**
   * An admin deactivated an enrollment. The parent-side equivalent
   * (`program_unenroll_start` / `_complete`) has been logged since the beginning; this side
   * was silent, so a row going inactive had no attributable cause.
   */
  trackAdminUnenroll: (params: {
    enrollmentId: string;
    programId?: string | null;
    programName?: string | null;
    studentId?: string | null;
    studentName?: string | null;
    /** Where the unenroll was triggered from, e.g. 'enrollments_table', 'student_detail'. */
    source?: string;
  }) => trackAsCurrentUser({
    eventType: 'admin_enrollment_unenroll',
    userType: 'admin',
    entityType: 'enrollment',
    entityId: params.enrollmentId,
    metadata: {
      program_id: params.programId ?? null,
      program_name: params.programName ?? null,
      student_id: params.studentId ?? null,
      student_name: params.studentName ?? null,
      source: params.source ?? null,
    },
  }),

  /** A parent added someone to a student's authorized pickup list. */
  trackAuthorizedPickupAdd: (params: AuthorizedPickupContext) =>
    trackAuthorizedPickupEvent('authorized_pickup_add', params),

  /**
   * A parent removed someone. The row is gone after this, so the event carries everything
   * needed to reconstruct who was authorized and until when.
   */
  trackAuthorizedPickupRemove: (params: AuthorizedPickupContext) =>
    trackAuthorizedPickupEvent('authorized_pickup_remove', params),

  /**
   * A photo was attached to, or cleared from, an authorized pickup contact.
   *
   * Worth its own event rather than folding into the add: the photo is what a teacher acts
   * on at the door, and "when did this face last change" is the question to be able to answer
   * if a handover is ever disputed.
   */
  trackAuthorizedPickupPhoto: (params: AuthorizedPickupContext & { cleared?: boolean }) =>
    trackAuthorizedPickupEvent(
      params.cleared ? 'authorized_pickup_photo_clear' : 'authorized_pickup_photo_set',
      params,
    ),

  // Parent Genie: parent opened the assistant page (fires on landing, before any question)
  trackAgentView: (params: {
    user?: User | null;
    /** Where the parent came from, e.g. 'flex_plan' for the flex-planner quick link. */
    source?: string;
  }) => trackEvent({
    eventType: 'parent_agent_view',
    user: params.user,
    userType: 'parent',
    entityType: 'agent',
    metadata: { source: params.source ?? 'direct' },
  }),

  // Parent Genie: helpfulness rating (1-3 stars) for an AI answer
  trackAgentFeedback: (params: {
    user?: User | null;
    rating: number;
    query: string;
    answer: string;
  }) => trackEvent({
    eventType: 'parent_agent_feedback',
    user: params.user,
    userType: 'parent',
    entityType: 'agent',
    metadata: {
      rating: params.rating,
      max_rating: 3,
      query: params.query,
      answer: params.answer,
    },
  }),

  // Payment events
  trackPaymentStart: (params: {
    user?: User | null;
    invoiceId: string | number;
    amount: number;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'payment_start',
    user: params.user,
    entityType: 'invoice',
    entityId: params.invoiceId,
    sessionId: params.sessionId,
    metadata: {
      amount: params.amount,
      ...params.metadata
    }
  }),

  trackPaymentComplete: (params: {
    user?: User | null;
    paymentId?: string | number;
    invoiceId?: string | number;
    amount: number;
    paymentMethod: string;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'payment_complete',
    user: params.user,
    entityType: 'payment',
    entityId: params.paymentId || params.invoiceId,
    sessionId: params.sessionId,
    metadata: {
      amount: params.amount,
      payment_method: params.paymentMethod,
      invoice_id: params.invoiceId,
      ...params.metadata
    }
  }),

  trackPaymentFailed: (params: {
    user?: User | null;
    invoiceId?: string | number;
    amount?: number;
    paymentMethod?: string;
    errorMessage: string;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'payment_failed',
    user: params.user,
    entityType: 'invoice',
    entityId: params.invoiceId,
    sessionId: params.sessionId,
    success: false,
    errorMessage: params.errorMessage,
    metadata: {
      amount: params.amount,
      payment_method: params.paymentMethod,
      ...params.metadata
    }
  }),

  // Payment method events
  trackAddPaymentMethod: (params: {
    user?: User | null;
    paymentMethod: string;
    setupIntentId?: string;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'add_payment_method',
    user: params.user,
    entityType: 'payment_method',
    sessionId: params.sessionId,
    metadata: {
      payment_method: params.paymentMethod,
      setup_intent_id: params.setupIntentId,
      ...params.metadata
    }
  }),

  trackUpdatePaymentMethod: (params: {
    user?: User | null;
    paymentMethod: string;
    setupIntentId?: string;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'update_payment_method',
    user: params.user,
    entityType: 'payment_method',
    sessionId: params.sessionId,
    metadata: {
      payment_method: params.paymentMethod,
      setup_intent_id: params.setupIntentId,
      ...params.metadata
    }
  }),

  // Invoice events
  trackInvoiceView: (params: {
    user?: User | null;
    invoiceId: string | number;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'invoice_view',
    user: params.user,
    entityType: 'invoice',
    entityId: params.invoiceId,
    sessionId: params.sessionId,
    metadata: params.metadata
  }),

  trackInvoiceDownload: (params: {
    user?: User | null;
    invoiceId: string | number;
    downloadType?: 'print' | 'pdf' | 'view';
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }) => trackEvent({
    eventType: 'invoice_download',
    user: params.user,
    entityType: 'invoice',
    entityId: params.invoiceId,
    sessionId: params.sessionId,
    metadata: {
      download_type: params.downloadType || 'print',
      ...params.metadata
    }
  }),

  trackAutopayToggle: (params: { user?: User | null; enabled: boolean }) =>
    trackEvent({
      eventType: 'autopay_toggle',
      user: params.user,
      metadata: { enabled: params.enabled }
    }),

  trackSetDefaultPaymentMethod: (params: { user?: User | null; method: 'card' | 'bank_account' | 'zelle' }) =>
    trackEvent({
      eventType: 'set_default_payment_method',
      user: params.user,
      entityType: 'payment_method',
      metadata: { method: params.method }
    }),

  // Feature flag override events (admin only)
  trackFeatureFlagOverride: (params: {
    flag: string;
    value: 'on' | 'off' | 'default';
    canonicalValue: boolean;
  }) => trackAsCurrentUser({
    eventType: 'feature_flag_override',
    userType: 'admin',
    metadata: {
      flag: params.flag,
      override_value: params.value,
      canonical_value: params.canonicalValue,
    },
  }),

  // Agent events
  trackAgentQuery: (params: {
    user?: User | null;
    query: string;
    fromCache: boolean;
    isSqlBased: boolean;
    success: boolean;
    processingTimeMs?: number;
    errorMessage?: string;
  }) => trackEvent({
    eventType: 'agent_query',
    user: params.user,
    userType: 'admin',
    entityType: 'agent',
    success: params.success,
    errorMessage: params.errorMessage,
    processingTimeMs: params.processingTimeMs,
    metadata: {
      query: params.query,
      from_cache: params.fromCache,
      is_sql_based: params.isSqlBased,
    }
  }),
};